import React, { useEffect, useRef } from 'react'
import { useDebug } from './DebugContext'
import RolePreviewSelector from './RolePreviewSelector'
import './Header.css'

const AccountSettings = ({ isOpen, onClose }) => {
  const accountSettingsModalRef = useRef(null)
  const { debugEnabled, setDebugEnabled } = useDebug()

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (accountSettingsModalRef.current && !accountSettingsModalRef.current.contains(event.target) && isOpen) {
        if (event.target.classList.contains('valuation-profile-modal-overlay')) {
          onClose()
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Theme toggle functionality
  useEffect(() => {
    if (!isOpen) return

    const themeUtils = window.__themeUtils || {}
    const THEME_KEY = themeUtils.THEME_KEY || "valuai-theme"
    const THEME_LIGHT = themeUtils.THEME_LIGHT || "light"
    const THEME_DARK = themeUtils.THEME_DARK || "dark"

    function getSystemTheme() {
      if (themeUtils.getSystemTheme) return themeUtils.getSystemTheme()
      return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? THEME_DARK
        : THEME_LIGHT
    }

    function getCurrentTheme() {
      if (themeUtils.getCurrentTheme) return themeUtils.getCurrentTheme()
      const savedTheme = localStorage.getItem(THEME_KEY)
      return savedTheme || getSystemTheme()
    }

    function applyTheme(theme) {
      if (themeUtils.applyTheme) {
        themeUtils.applyTheme(theme)
        return
      }
      // Fallback if theme utils not available
      const root = document.documentElement
      root.setAttribute("data-theme", theme)
      localStorage.setItem(THEME_KEY, theme)
    }

    function toggleTheme() {
      if (themeUtils.toggleTheme) {
        themeUtils.toggleTheme()
        return
      }
      // Fallback: read actual current theme from DOM
      const currentTheme = document.documentElement.getAttribute("data-theme") || getCurrentTheme()
      const newTheme = currentTheme === THEME_LIGHT ? THEME_DARK : THEME_LIGHT
      applyTheme(newTheme)
    }

    // Attach theme toggle handler to account settings theme button
    const handleThemeToggle = (e) => {
      e.preventDefault()
      e.stopPropagation()
      toggleTheme()
    }

    const accountSettingsThemeToggle = document.getElementById("accountSettingsThemeToggle")
    if (accountSettingsThemeToggle) {
      accountSettingsThemeToggle.addEventListener("click", handleThemeToggle)
      return () => {
        accountSettingsThemeToggle.removeEventListener("click", handleThemeToggle)
      }
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="valuation-profile-modal-overlay" onClick={onClose}>
      <div className="valuation-profile-modal" ref={accountSettingsModalRef} onClick={(e) => e.stopPropagation()}>
        <div className="valuation-profile-modal-header">
          <h2>Account Settings</h2>
          <button 
            className="valuation-profile-modal-close"
            onClick={onClose}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="valuation-profile-modal-content">
          {/* Account Settings Section */}
          <div className="valuation-profile-section">
            <div className="valuation-profile-section-header">
              <h3 className="valuation-profile-section-title">Theme</h3>
            </div>
            <div className="valuation-profile-info-grid">
              <div className="valuation-profile-info-item">
                <label className="valuation-profile-info-label">Appearance</label>
                <div className="valuation-profile-info-value" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span>Switch between light and dark mode</span>
                  <button
                    id="accountSettingsThemeToggle"
                    aria-label="Toggle theme"
                    className="profile-theme-toggle"
                  >
                    <svg
                      className="profile-theme-icon-light"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="5"></circle>
                      <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"></path>
                    </svg>
                    <svg
                      className="profile-theme-icon-dark"
                      width="16"
                      xmlns="http://www.w3.org/2000/svg"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Debugging */}
          <div className="valuation-profile-section">
            <div className="valuation-profile-section-header">
              <h3 className="valuation-profile-section-title">Debugging</h3>
            </div>
            <div className="valuation-profile-info-grid">
              <div className="valuation-profile-info-item">
                <label className="valuation-profile-info-label" htmlFor="accountSettingsDebugToggle">
                  Enable debug tools
                </label>
                <div className="valuation-profile-info-value" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <span>Show view-as controls, sidebar role colors, and other debug UI (admin only)</span>
                  <button
                    id="accountSettingsDebugToggle"
                    type="button"
                    role="switch"
                    aria-checked={debugEnabled}
                    aria-label={debugEnabled ? 'Disable debugging' : 'Enable debugging'}
                    className={`profile-toggle-switch ${debugEnabled ? 'profile-toggle-switch-on' : ''}`}
                    onClick={() => setDebugEnabled(!debugEnabled)}
                  >
                    <span className="profile-toggle-switch-track">
                      <span className="profile-toggle-switch-thumb" />
                    </span>
                  </button>
                </div>
              </div>
              {debugEnabled && (
                <div className="valuation-profile-info-item">
                  <label className="valuation-profile-info-label">View as role</label>
                  <div className="valuation-profile-info-value">
                    <span style={{ display: 'block', marginBottom: '0.75rem' }}>
                      Preview the app UI as another account type
                    </span>
                    <RolePreviewSelector />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AccountSettings

